import React, { useState, useEffect } from 'react';
import { useDialogueStore } from '../store/dialogueStore';
import { DialogueSection, DialogueCondition, Command } from '../types/dialogue';
import { Plus, Trash2, Save, ChevronDown, ChevronRight, Code, MessageSquare, GitBranch, AlertCircle } from 'lucide-react';
import { ConditionEditor } from './ui/ConditionEditor';
import { useToast } from '../contexts/ToastContext';

const DialogueEditor: React.FC = () => {
  const { dialogueData, selectedNode, updateDialogueSection } = useDialogueStore();
  const { showSuccess, showError, showWarning } = useToast();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Load expanded sections from localStorage
    const saved = localStorage.getItem('expandedSections');
    return saved ? new Set(JSON.parse(saved)) : new Set(['triggers', 'conditions', 'default']);
  });
  const [currentSection, setCurrentSection] = useState<DialogueSection | null>(null);
  const [sectionKey, setSectionKey] = useState<string>('');
  const [conditionErrors, setConditionErrors] = useState<Record<number, string[]>>({});

  useEffect(() => {
    if (selectedNode && dialogueData) {
      const match = selectedNode.match(/^dialogue-(.+)$/);
      if (match) {
        const key = match[1];
        setSectionKey(key);
        setCurrentSection(dialogueData.dialogues[key] || null);
      }
    }
  }, [selectedNode, dialogueData]);

  useEffect(() => {
    // Save expanded sections to localStorage
    localStorage.setItem('expandedSections', JSON.stringify(Array.from(expandedSections)));
  }, [expandedSections]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const updateTriggers = (triggers: string[]) => {
    if (currentSection) {
      setCurrentSection({ ...currentSection, triggers });
    }
  };

  const updateCondition = (index: number, condition: DialogueCondition) => {
    if (currentSection) {
      const newConditions = [...currentSection.conditions];
      newConditions[index] = condition;
      setCurrentSection({ ...currentSection, conditions: newConditions });
    }
  };

  const addCondition = () => {
    if (currentSection) {
      const newCondition: DialogueCondition = {
        condition: 'new_condition',
        response: 'New response',
        commands: []
      };
      setCurrentSection({
        ...currentSection,
        conditions: [...currentSection.conditions, newCondition]
      });
    }
  };

  const removeCondition = (index: number) => {
    if (currentSection) {
      const newConditions = currentSection.conditions.filter((_, i) => i !== index);
      setCurrentSection({ ...currentSection, conditions: newConditions });
    }
  };

  const addCommand = (conditionIndex: number) => {
    if (currentSection) {
      const newCommand: Command = { type: 'set_flag', flag: 'new_flag', value: true };
      const newConditions = [...currentSection.conditions];
      newConditions[conditionIndex].commands.push(newCommand);
      setCurrentSection({ ...currentSection, conditions: newConditions });
    }
  };

  const saveChanges = () => {
    if (currentSection && sectionKey) {
      // Check for validation errors
      const hasErrors = Object.values(conditionErrors).some(errors => errors.length > 0);
      
      if (hasErrors) {
        showWarning('Cannot save', 'Please fix validation errors before saving');
        return;
      }
      
      try {
        updateDialogueSection(sectionKey, currentSection);
        showSuccess('Saved successfully', `Changes to "${sectionKey}" have been saved`);
      } catch (error) {
        showError('Save failed', 'An error occurred while saving changes');
        console.error('Save error:', error);
      }
    }
  };

  if (!currentSection) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">Select a dialogue node to edit</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Editing: <span className="text-blue-600 dark:text-blue-400">{sectionKey}</span>
        </h3>
      </div>
      
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {/* Triggers Section */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('triggers')}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Code size={16} />
              Triggers
              <span className="text-xs text-gray-500 dark:text-gray-400">({currentSection.triggers.length})</span>
            </span>
            {expandedSections.has('triggers') ? 
              <ChevronDown size={16} className="text-gray-500" /> : 
              <ChevronRight size={16} className="text-gray-500" />
            }
          </button>
          {expandedSections.has('triggers') && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <textarea
                value={currentSection.triggers.join(', ')}
                onChange={(e) => updateTriggers(e.target.value.split(',').map(t => t.trim()).filter(t => t))}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-gray-100 font-mono"
                placeholder="Enter triggers separated by commas"
                rows={2}
              />
            </div>
          )}
        </div>

        {/* Conditions Section */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
            <button
              onClick={() => toggleSection('conditions')}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            >
              {expandedSections.has('conditions') ? 
                <ChevronDown size={16} /> : 
                <ChevronRight size={16} />
              }
              <GitBranch size={16} />
              Conditions
              <span className="text-xs text-gray-500 dark:text-gray-400">({currentSection.conditions.length})</span>
            </button>
            <button
              onClick={addCondition}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded transition-colors"
            >
              <Plus size={14} /> Add
            </button>
          </div>

          {expandedSections.has('conditions') && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            {currentSection.conditions.map((condition, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 overflow-hidden"
              >
                <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 flex items-center justify-between">
                  <button
                    onClick={() => toggleSection(`condition-${index}`)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                  >
                    {expandedSections.has(`condition-${index}`) ? 
                      <ChevronDown size={16} /> : 
                      <ChevronRight size={16} />
                    }
                    Condition {index + 1}
                    {conditionErrors[index]?.length > 0 && (
                      <AlertCircle size={14} className="text-red-500" />
                    )}
                  </button>
                  <button
                    onClick={() => removeCondition(index)}
                    className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {expandedSections.has(`condition-${index}`) && (
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Condition Expression
                      </label>
                      <ConditionEditor
                        value={condition.condition}
                        onChange={(value) => updateCondition(index, { ...condition, condition: value })}
                        onValidate={(isValid, errors) => {
                          setConditionErrors(prev => ({
                            ...prev,
                            [index]: isValid ? [] : errors
                          }));
                        }}
                        height="80px"
                      />
                      {conditionErrors[index]?.length > 0 && (
                        <div className="mt-1 flex items-start gap-1 text-xs text-red-600 dark:text-red-400">
                          <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                          <span>{conditionErrors[index].join(', ')}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Response
                      </label>
                      <textarea
                        value={condition.response}
                        onChange={(e) => updateCondition(index, { ...condition, response: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-gray-100"
                        rows={3}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Commands
                        </label>
                        <button
                          onClick={() => addCommand(index)}
                          className="p-1 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded transition-colors"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="space-y-1">
                        {condition.commands.map((cmd, cmdIndex) => (
                          <div 
                            key={cmdIndex} 
                            className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded font-mono"
                          >
                            {cmd.type}: {cmd.flag || cmd.counter || cmd.item || cmd.quest || 'N/A'}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          )}
        </div>

        {/* Default Response Section */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('default')}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <MessageSquare size={16} />
              Default Response
            </span>
            {expandedSections.has('default') ? 
              <ChevronDown size={16} className="text-gray-500" /> : 
              <ChevronRight size={16} className="text-gray-500" />
            }
          </button>
          {expandedSections.has('default') && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <textarea
                value={currentSection.default.response}
                onChange={(e) => setCurrentSection({
                  ...currentSection,
                  default: { ...currentSection.default, response: e.target.value }
                })}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-gray-100"
                rows={3}
              />
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={saveChanges}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
        >
          <Save size={16} /> Save Changes
        </button>
      </div>
    </div>
  );
};

export default DialogueEditor;